import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "@shared/v2/Button";
import TextButton from "@shared/v2/TextButton";
import TextLink from "@shared/v2/TextLink";
import { Mailboxflagupsolid, FeatherRegularV6 } from "@shared/v2/Icomoon/Icons";
import Tooltip from "@shared/v2/Tooltip";
import AddressPickerModal from "@shared/v2/AddressPickerModal";
import { isMailableAddress } from "@shared/v2/utils";
import {
  useHandwrittenCard,
  useHandwrittenCardPreview,
  HandwrittenCardModal,
  getAddressSortPriority,
  getInvalidReason,
} from "@shared/v2/HandwrittenCard";
import { useStandardizedAddresses } from "@shared/v2/hooks";
import CollapsibleElement from "../CollapsibleElement";
import { getMarketerOnboardingStatus } from "../../actions/personDetailActionCreators";
import { getInkedContactPreviewLinkApi } from "../../shared/api";
import { useFeatureFlags } from "../../../reducers/layoutReducer/selectors";
import SelectMailerTypeModal from "../modals/SelectMailerTypeModal";
import { mapToContactPayload } from "../listing_alerts/helper";
import Loading from "../../../Goals/components/Loader";

const Marketing = () => {
  const dispatch = useDispatch();
  const person = useSelector((state) => state.personDetail.person);
  const featureFlags = useFeatureFlags();
  const canSendHandwrittenCard = featureFlags.sendHandwrittenCard;

  const {
    isLoading: isLoadingHandwrittenCard,
    iframeUrl: handwrittenCardIframeUrl,
    iframeRef,
    error: handwrittenCardError,
    isAddressPickerOpen,
    isModalOpen: isHandwrittenCardModalOpen,
    fetchIframeUrl,
    closeModal: closeHandwrittenCardModal,
    openAddressPicker,
    closeAddressPicker,
  } = useHandwrittenCard();

  const {
    title: previewTitle,
    isLoading: isLoadingPreview,
    signedUrl: previewSignedUrl,
    error: previewError,
    isPreviewOpen,
    openPreview,
    closePreview,
  } = useHandwrittenCardPreview();

  const { addresses, standardizedAddresses, isLoading: isLoadingStandardization } =
    useStandardizedAddresses(person, person?.data?.attributes?.slug);

  const sortedAddresses = useMemo(
    () =>
      [...standardizedAddresses].sort((a, b) => getAddressSortPriority(a) - getAddressSortPriority(b)),
    [standardizedAddresses],
  );
  const hasAddresses = addresses.length > 0;
  const mailableAddresses = useMemo(() => addresses.filter(isMailableAddress), [addresses]);
  const canSendMailer = mailableAddresses.length > 0;
  const hasFirstName = Boolean(person?.data?.attributes?.first_name?.trim());

  const [isModalOpen, setModalOpen] = useState(false);
  const marketerOnboardingStatus = useSelector((state) => state.personDetail.marketerOnboardingStatus);
  const [isOnboardingStatusLoaded, setIsOnboardingStatusLoaded] = useState(false);

  const isLoading = !person?.data || !isOnboardingStatusLoaded;

  const openSelectMailerTypeModal = () => {
    if (isModalOpen) {
      setModalOpen(false);
    } else {
      setModalOpen(true);
    }
  };

  useEffect(() => {
    dispatch(getMarketerOnboardingStatus()).finally(() => setIsOnboardingStatusLoaded(true));
  }, []);

  const { team_onboarded: teamOnboarded, agent_onboarded: agentOnboarded } =
    marketerOnboardingStatus?.data || {};
  const isOnboarded = teamOnboarded || agentOnboarded;

  const getDisabledTooltip = (type, requiresFirstName = false) => {
    const needsFirstName = requiresFirstName && !hasFirstName;
    if (canSendMailer && !needsFirstName) return null;

    if (needsFirstName && canSendMailer) {
      return `Add a first name to this lead to start sending ${type}.`;
    }
    if (!needsFirstName) {
      return hasAddresses
        ? `Update this lead to have a valid mailing address to start sending ${type}.`
        : `Add an address to this lead to start sending ${type}.`;
    }
    return hasAddresses
      ? `Add a first name to this lead and update it to have a valid mailing address to start sending ${type}.`
      : `Add a first name and an address to this lead to start sending ${type}.`;
  };

  const handleSendHandwrittenCard = () => {
    if (!hasFirstName) return;
    if (addresses.length === 1 && mailableAddresses.length === 1) {
      fetchIframeUrl(person?.data?.attributes?.uuid, mailableAddresses[0].id);
    } else if (addresses.length >= 2 && mailableAddresses.length >= 1) {
      openAddressPicker();
    }
  };

  // Inked's screen in contact mode: every card sent to this lead, newest first.
  const handleViewSentCards = (clickEvent) => {
    clickEvent.preventDefault();
    openPreview("Handwritten Cards Sent", () => getInkedContactPreviewLinkApi(person?.data?.attributes?.uuid));
  };

  const handleAddressSelect = (address) => {
    closeAddressPicker();
    fetchIframeUrl(person?.data?.attributes?.uuid, address.id);
  };

  const btnComponent = (
    <Button
      schema="secondary"
      className="tw-w-full tw-h-[44px] !tw-rounded-[8px] tw-flex !tw-gap-[8px] tw-items-center tw-justify-center tw-text-14d !tw-normal-case"
      onClick={openSelectMailerTypeModal}
      disabled={!canSendMailer}
    >
      <Mailboxflagupsolid size="l" />
      <span>Send Mail</span>
    </Button>
  );
  if (isLoading) {
    return (
      <CollapsibleElement id="pdp-marketing" title="Marketing">
        <Loading />
      </CollapsibleElement>
    );
  }

  return (
    <CollapsibleElement id="pdp-marketing" title="Marketing">
      <div className="tw-grid tw-gap-y-[24px]">
        <div>
          Send postcards, bifolds, trifolds, and snap packs with{" "}
          <TextButton
            className="tw-normal-case tw-text-[12px] tw-font-normal tw-p-0"
            onClick={() => window.open(`/launch?slug=brivity-marketer`, "_blank")}
          >
            Brivity Marketer
          </TextButton>
          {canSendHandwrittenCard ? (
            <>
              , or a handwritten card with{" "}
              <TextButton
                className="tw-normal-case tw-text-[12px] tw-font-normal tw-p-0"
                onClick={() => window.open("https://app.inked.com/get-started", "_blank")}
              >
                inked!
              </TextButton>
            </>
          ) : (
            <>.</>
          )}
        </div>
        {!isOnboarded && (
          <div className="tw-border-1px tw-border-solid tw-border-[#FAE4C3] tw-bg-[#FAF1E6] tw-rounded-[4px]  tw-items-center tw-py-[12px] tw-px-[16px]">
            <div className="tw-text-[#AD6714] tw-text-12d tw-flex tw-flex-col tw-gap-[4px]">
              <div className="">To start sending mailers, an admin must complete Marketer onboarding.</div>
            </div>

            <Button
              schema="semantic-yellow"
              className="tw-shrink-0 tw-mt-[4px]"
              onClick={() => window.open(`/launch?slug=brivity-marketer`, "_blank")}
            >
              complete onboarding
            </Button>
          </div>
        )}
        {isOnboarded && (
          <div className="tw-grid tw-gap-y-[16px]">
            <Tooltip
              placement="top"
              multiline
              innerClassName="tw-max-w-[200px]"
              trigger={btnComponent}
              content={getDisabledTooltip("mail")}
            />

            {canSendHandwrittenCard && (
              <div className="tw-grid tw-gap-y-[4px]">
                <Tooltip
                  placement="top"
                  multiline
                  innerClassName="tw-max-w-[200px]"
                  trigger={
                    <Button
                      schema="secondary"
                      className="tw-w-full tw-h-[44px] !tw-rounded-[8px] tw-flex !tw-gap-[8px] tw-items-center tw-justify-center tw-text-14d !tw-normal-case"
                      isLoading={isLoadingHandwrittenCard}
                      onClick={handleSendHandwrittenCard}
                      disabled={!canSendMailer || !hasFirstName}
                    >
                      <FeatherRegularV6 size="m" />
                      <span>Send Handwritten Card</span>
                    </Button>
                  }
                  content={getDisabledTooltip("handwritten cards", true)}
                />

                <div className="tw-text-center">
                  <TextLink onClick={handleViewSentCards} size="small">
                    View handwritten cards sent
                  </TextLink>
                </div>
              </div>
            )}
          </div>
        )}
        <SelectMailerTypeModal
          setOpen={openSelectMailerTypeModal}
          isModalOpen={isModalOpen}
          personData={mapToContactPayload([{ ...person?.data?.attributes, included: person?.included }])}
        />

        <AddressPickerModal
          show={isAddressPickerOpen}
          onHide={closeAddressPicker}
          addresses={sortedAddresses}
          onSelect={handleAddressSelect}
          getInvalidReason={getInvalidReason}
          isLoading={isLoadingStandardization}
        />

        <HandwrittenCardModal
          show={isHandwrittenCardModalOpen}
          onHide={closeHandwrittenCardModal}
          isLoading={isLoadingHandwrittenCard}
          iframeUrl={handwrittenCardIframeUrl}
          iframeRef={iframeRef}
          error={handwrittenCardError}
        />

        <HandwrittenCardModal
          show={isPreviewOpen}
          onHide={closePreview}
          isLoading={isLoadingPreview}
          iframeUrl={previewSignedUrl}
          error={previewError}
          title={previewTitle}
        />
      </div>
    </CollapsibleElement>
  );
};

export default Marketing;
